import { merge, isFunction } from 'lodash';
import { api } from '@/api/common/instances';
import { comma, kilobytes } from '@/utils/formatter';
import motletable from '@/mixins/motletable';
import chartable from '@/mixins/chartable';
import BarChart from '../../../components/BarChart';

export default {
  components: {
    BarChart,
  },
  mixins: [motletable, chartable],
  props: {
    sstmId: {
      type: String,
      required: true,
    },
    storeName: {
      type: String,
      required: true,
    },
    timestamp: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      data: [],
      motletOptions: {
        monitor: {
          systemId: this.sstmId,
        },
        descendant: {
          systemId: this.sstmId,
        },
      },
      // BEGIN::Added.by.KWShin.2024.03.13.
      datetime: null,
      // FINISH::Added.by.KWShin.2024.03.13.
    };
  },
  computed: {
    descendants() {
      return this.$store.getters[`${this.storeName}/get`];
    },
    categoryField() {
      return this.motletState.monitor.categoryField;
    },
    settings() {
      if (this.data.length == 0) return {};

      const percent = this.chartField.type == 'PERCENT';
      const xAxis = {};
      const yAxis = {
        name: `${this.chartField.text} ${percent ? '(%)' : ''}`,
        max: percent ? 100 : undefined,
        maxInterval: percent ? 25 : undefined,
        splitNumber: percent ? 4 : undefined,
      };
      const dataset = (() => {
        // data 내의 모든 categoryField 를 취합하여 중복제거 & 정렬.
        const categories = [...new Set(this.data.reduce((acc, item) => acc.concat(item.data.map(s => s[this.categoryField])), []))].sort();
        const header = ['name'].concat(this.descendants.map(d => d.id));
        const dataset = categories.map(category => {
          return [category].concat(
            this.descendants
              .filter(descendant => descendant.active)
              .map(descendant => {
                const index = this.descendants.findIndex(item => item.id == descendant.id);
                const target = this.data[index] ? this.data[index].data.find(item => item[this.categoryField] == category) : null;
                return (target || {})[this.chartField.code];
              }),
          );
        });
        return { source: [header].concat(dataset) };
      })();
      const series = this.descendants
        .filter(descendant => descendant.active)
        .map(descendant => {
          return {
            name: descendant.id,
            type: 'bar',
            itemStyle: {
              color: descendant.color,
            },
          };
        });
      const valueFormatter = (data, type = this.chartField.type) => {
        const formatted = {
          unit: '',
          data: '',
        };
        const isEmpty = data === undefined || data === null || data === '';
        data = isEmpty ? '' : data;
        if (type == 'PERCENT') {
          formatted.unit = isEmpty ? '' : ' %';
          formatted.data = data;
        } else if (type == 'DIGIT') {
          formatted.data = comma(data);
        } else if (type == 'KILO_BYTE') {
          formatted.data = kilobytes(data);
        } else {
          formatted.data = data;
        }
        return `${formatted.data}${formatted.unit}`;
      };
      const formatter = {
        tooltip: params => {
          const param = params[0];
          const contents = [];
          contents.push('<i class="v-icon notranslate mdi mdi-harddisk mr-1" style="font-size: 12px; line-height: 10px"></i>');
          contents.push(param.name);
          contents.push('<br />');

          params.forEach((p, index) => {
            if (p.data[index + 1] === undefined) return;
            const color = this.descendants.find(d => d.id == p.seriesName).color;
            contents.push(this.circle(color));
            contents.push(`<span style="color:${color};font-weight:900;">`);
            // contents.push(p.seriesName);
            contents.push(this.descendants.find(d => d.id == p.seriesName).shortAlias);
            contents.push('</span> : ');
            contents.push(valueFormatter(p.data[index + 1]));
            contents.push('<br />');
          });

          return contents.join('');
        },
      };
      const defaults = {
        chart: {
          xAxis,
          yAxis,
          dataset,
          series,
        },
        formatter,
      };
      return merge({}, defaults, isFunction(this.getSettings) ? this.getSettings() : undefined);
    },
  },
  watch: {
    // BEGIN::Added.by.KWShin.2024.03.13.
    sstmId: {
      handler() {
        if (this.datetime) this.fetchData(this.datetime);
      },
    },
    // FINISH::Added.by.KWShin.2024.03.13.
    timestamp(value) {
      this.fetchData(value);
    },
  },
  methods: {
    async fetchData(timestamp) {
      if (!this.datetime) this.datetime = timestamp;
      const sstmId = this.sstmId;
      const motletCode = this.monitor.monitorCode;
      const params = {
        endYmdhms: timestamp,
        sstmId,
        motletCode,
      };
      const rslt = await api.get('/motlet-data/chart', { params });
      const { data: results } = rslt;
      this.data = results.map(result => {
        const {
          key: { instId: id },
          data,
        } = result;
        return { id, data };
      });
    },
  },
};
